import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, RefreshCw, Plus, Check } from "lucide-react";
import {
  fetchAdminOrgs,
  syncNow,
  registerOrg,
  createOrg,
  type AdminOrg,
} from "@/api/admin";

export function AdminOrgs() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-orgs"],
    queryFn: fetchAdminOrgs,
  });

  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-orgs"] });

  const sync = useMutation({
    mutationFn: syncNow,
    onSuccess: (r) => {
      setMsg({ kind: "ok", text: `Synced ${r.orgs_synced} orgs, ${r.devices_synced} devices.` });
      refresh();
      qc.invalidateQueries({ queryKey: ["admin-devices"] });
    },
    onError: (e: unknown) => setMsg({ kind: "err", text: `Sync failed: ${String(e)}` }),
  });

  const register = useMutation({
    mutationFn: (id: string) => registerOrg(id),
    onSuccess: () => { setMsg({ kind: "ok", text: "Organization registered." }); refresh(); },
    onError: (e: unknown) => setMsg({ kind: "err", text: `Register failed: ${String(e)}` }),
  });

  const orgs = data?.orgs ?? [];

  return (
    <div className="p-lg space-y-md">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text flex items-center gap-sm">
            <Building2 size={20} className="text-brand" /> Organizations
          </h1>
          <p className="text-sm text-textLight">
            ait-auth organizations and their registration with kraken.
          </p>
        </div>
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="flex items-center gap-xs px-md py-sm rounded-md bg-brand text-white text-sm hover:bg-brand-dark disabled:opacity-50 cursor-pointer"
        >
          <RefreshCw size={14} className={sync.isPending ? "animate-spin" : ""} /> Sync now
        </button>
      </header>

      {msg && (
        <div
          className={`px-md py-sm rounded-md text-sm border ${
            msg.kind === "ok"
              ? "bg-success-bg text-success-text border-success-text/20"
              : "bg-danger-bg text-danger-text border-danger-text/20"
          }`}
        >
          {msg.text}
        </div>
      )}

      <CreateOrgForm
        onCreated={(name) => { setMsg({ kind: "ok", text: `Created "${name}".` }); refresh(); }}
        onError={(t) => setMsg({ kind: "err", text: t })}
      />

      <div className="rounded-lg border border-border bg-surface shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg text-textLight text-left">
            <tr>
              <th className="px-md py-sm font-medium">Name</th>
              <th className="px-md py-sm font-medium">Slug</th>
              <th className="px-md py-sm font-medium">Status</th>
              <th className="px-md py-sm font-medium">Registered</th>
              <th className="px-md py-sm" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-md py-lg text-center text-textLight">Loading…</td></tr>
            )}
            {isError && (
              <tr><td colSpan={5} className="px-md py-lg text-center text-danger-text">
                {String(error)}
              </td></tr>
            )}
            {!isLoading && !isError && orgs.length === 0 && (
              <tr><td colSpan={5} className="px-md py-lg text-center text-textLight">No organizations.</td></tr>
            )}
            {orgs.map((o: AdminOrg) => (
              <tr key={o.id} className="border-t border-border">
                <td className="px-md py-sm text-text">{o.name}</td>
                <td className="px-md py-sm text-textLight">{o.slug ?? "—"}</td>
                <td className="px-md py-sm text-textLight">{o.status}</td>
                <td className="px-md py-sm">
                  {o.is_registered ? (
                    <span className="inline-flex items-center gap-xs text-success-text">
                      <Check size={14} /> Yes
                    </span>
                  ) : (
                    <span className="text-textLight">No</span>
                  )}
                </td>
                <td className="px-md py-sm text-right">
                  {!o.is_registered && (
                    <button
                      type="button"
                      onClick={() => register.mutate(o.id)}
                      disabled={register.isPending}
                      className="px-sm py-xs rounded-md border border-border text-sm hover:border-brand/40 hover:text-brand cursor-pointer"
                    >
                      Register
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateOrgForm({
  onCreated,
  onError,
}: {
  onCreated: (name: string) => void;
  onError: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => createOrg(slug.trim(), name.trim()),
    onSuccess: () => { onCreated(name.trim()); setOpen(false); setSlug(""); setName(""); },
    onError: (e: unknown) => onError(`Create failed: ${String(e)}`),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-xs px-md py-sm rounded-md border border-border text-sm hover:border-brand/40 hover:text-brand cursor-pointer"
      >
        <Plus size={14} /> Create organization
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (slug.trim() && name.trim()) create.mutate(); }}
      className="flex flex-wrap items-end gap-sm p-md rounded-lg border border-border bg-surface shadow-card"
    >
      <label className="flex flex-col gap-xs">
        <span className="text-xs text-textLight">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="px-sm py-xs rounded-md border border-border text-sm"
          placeholder="Acme Water Authority"
        />
      </label>
      <label className="flex flex-col gap-xs">
        <span className="text-xs text-textLight">Slug</span>
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="px-sm py-xs rounded-md border border-border text-sm"
          placeholder="acme"
        />
      </label>
      <button
        type="submit"
        disabled={create.isPending || !slug.trim() || !name.trim()}
        className="px-md py-xs rounded-md bg-brand text-white text-sm hover:bg-brand-dark disabled:opacity-50 cursor-pointer"
      >
        Create
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="px-md py-xs rounded-md border border-border text-sm hover:bg-bg cursor-pointer"
      >
        Cancel
      </button>
    </form>
  );
}

export default AdminOrgs;
